import { useQuery } from "@apollo/client";
import { useWithDefaultParams } from "Services/useWithDefaultParams";
import {
	FILTERS_TARIFFS_ADDRESS_QUERY,
	REVIEWS_FAST_QUERY,
	HOUSE_FULL_QUERY_V4,
	STREET_ONE_QUERY,
	DECLINATION_QUERY,
	PROVIDERS_V3_BY_STREET,
	OPERATORS_QUERY,
	OPERATOR_HEADER_QUERY,
} from "Services/queries";
import { useMemo } from "react";

export const useReviewsData = (queryType, reviewLocation) => {
	const { reviewsFast, isLoadingReviewsFast } = useReviewsFastQuery(queryType, reviewLocation);
	const { providersListUniqueHouse, isLoadingProvidersListHouse } = useProvidersListHouseQuery(
		queryType,
		reviewLocation
	);
	const { providersListStreet, isLoadingProvidersListStreet } = useProvidersListStreetQuery(
		queryType,
		reviewLocation
	);
	const { operatorsList, isLoadingOperatorsList } = useOperatorsQuery(queryType, reviewLocation);
	const { operatorOne, isLoadingOperatorOne } = useOperatorOneQuery(queryType, reviewLocation);
	const { houseInfo, isLoadingHouseInfo } = useHouseInfoQuery(queryType, reviewLocation);
	const { streetInfo, isLoadingStreetInfo } = useStreetInfoQuery(queryType, reviewLocation);
	const { regionInfo, isLoadingRegionInfo } = useDeclinationRegionQuery(queryType, reviewLocation);

	const reviewsList = useMemo(() => {
		if (isLoadingReviewsFast) {
			return [];
		}

		return Boolean(reviewsFast.length) ? reviewsFast : [];
	}, [reviewsFast, isLoadingReviewsFast]);

	const providersList = useMemo(() => {
		if (
			isLoadingProvidersListHouse ||
			isLoadingProvidersListStreet ||
			isLoadingOperatorsList ||
			isLoadingOperatorOne
		) {
			return [];
		}

		return Boolean(providersListUniqueHouse.length)
			? providersListUniqueHouse
			: Boolean(providersListStreet.length)
				? providersListStreet
				: Boolean(operatorsList.length)
					? operatorsList
					: Boolean(operatorOne.id)
						? [operatorOne]
						: [];
	}, [
		providersListUniqueHouse,
		isLoadingProvidersListHouse,
		providersListStreet,
		isLoadingProvidersListStreet,
		operatorsList,
		isLoadingOperatorsList,
		operatorOne,
		isLoadingOperatorOne,
	]);

	const houseFullInfo = useMemo(() => {
		if (isLoadingHouseInfo) {
			return {};
		}
		return houseInfo;
	}, [houseInfo, isLoadingHouseInfo]);

	const streetFullInfo = useMemo(() => {
		if (isLoadingStreetInfo) {
			return {};
		}
		return streetInfo;
	}, [streetInfo, isLoadingStreetInfo]);

	const regionFullInfo = useMemo(() => {
		if (isLoadingRegionInfo) {
			return {};
		}
		return regionInfo;
	}, [regionInfo, isLoadingRegionInfo]);

	return {
		reviewsList,
		providersList,
		houseFullInfo,
		streetFullInfo,
		regionFullInfo,
	};
};

const useReviewsFastQuery = (queryType, reviewLocation) => {
	const {
		params: { house_id, streetId, regionUrl, operator },
	} = useWithDefaultParams();

	let skip = true;
	const variables = { sort: "-date", short: 1 };

	if (queryType === "reviews") {
		if (reviewLocation === "house" && house_id) {
			skip = false;
			variables.house_id = +house_id;
		} else if (reviewLocation === "street" && streetId) {
			skip = false;
			variables.street_id = +streetId;
		} else if (reviewLocation === "ratesmobile_region" && regionUrl) {
			skip = false;
			variables.region_url = regionUrl;
			variables.find_operator = true;
			variables.providerJoin = true;
		} else if (reviewLocation === "about_operator" && operator) {
			skip = false;
			variables.find_operator = true;
			variables.provider_url = operator;
			variables.region_url = regionUrl;
		}
	}

	let { data, loading } = useQuery(REVIEWS_FAST_QUERY, {
		skip,
		variables,
	});

	return {
		reviewsFast: data?.reviewsFast?.data || [],
		isLoadingReviewsFast: loading,
	};
};

const useProvidersListHouseQuery = (queryType, reviewLocation) => {
	const {
		params: { house_id },
	} = useWithDefaultParams();

	let { data, loading } = useQuery(FILTERS_TARIFFS_ADDRESS_QUERY, {
		skip:
			queryType !== "providers" ||
			reviewLocation !== "house" ||
			!Number.isInteger(parseInt(house_id)),
		variables: {
			houseId: +house_id,
		},
	});

	return {
		providersListUniqueHouse: data?.filtersTariffsAddress?.providers || [],
		isLoadingProvidersListHouse: loading,
	};
};

const useProvidersListStreetQuery = (queryType, reviewLocation) => {
	const {
		params: { streetId },
	} = useWithDefaultParams();

	let { data, loading } = useQuery(PROVIDERS_V3_BY_STREET, {
		skip: queryType !== "providers" || reviewLocation !== "street" || !streetId,
		variables: {
			street_id: +streetId,
			sort: "-vip_provider,vip_b2c_priority,-rating.expert.final,name",
			random: false,
		},
	});

	return {
		providersListStreet: data?.providersV3?.data || [],
		isLoadingProvidersList: loading,
	};
};

const useOperatorOneQuery = (queryType, reviewLocation) => {
	const {
		params: { regionUrl, operator },
	} = useWithDefaultParams();

	const { data, loading } = useQuery(OPERATOR_HEADER_QUERY, {
		skip:
			queryType !== "operator" ||
			(reviewLocation !== "about_operator" &&
				reviewLocation !== "operator_actions" &&
				reviewLocation !== "operator_ratesmobile") ||
			!regionUrl,
		variables: {
			filter: `region.url=${regionUrl}&url_name=${operator}&mobile_operator=1`,
		},
	});

	return {
		operatorOne: data?.provider || {},
		isLoadingOperatorOne: loading,
	};
};

const useOperatorsQuery = (queryType, reviewLocation) => {
	const {
		params: { regionUrl },
	} = useWithDefaultParams();

	const { data, loading } = useQuery(OPERATORS_QUERY, {
		skip: queryType !== "operators" || reviewLocation !== "ratesmobile_region" || !regionUrl,
		variables: {
			filter: `region.url=${regionUrl}&mobile_operator=1`,
			limit: 30,
			offset: 0,
		},
	});

	return {
		operatorsList: data?.operators?.data || [],
		isLoadingoperatorsList: loading,
	};
};

const useHouseInfoQuery = (queryType, reviewLocation) => {
	const {
		params: { house_id },
	} = useWithDefaultParams();

	let { data, loading } = useQuery(HOUSE_FULL_QUERY_V4, {
		skip:
			(queryType !== "houseFull" && queryType !== "reviews") ||
			reviewLocation !== "house" ||
			!Number.isInteger(parseInt(house_id)),
		variables: {
			id: parseInt(house_id),
		},
	});

	return {
		houseInfo: data?.houseFullV4 || {},
		isLoadingHouseInfo: loading,
	};
};

const useStreetInfoQuery = (queryType, reviewLocation) => {
	const {
		params: { streetId },
	} = useWithDefaultParams();

	let { data, loading } = useQuery(STREET_ONE_QUERY, {
		skip: queryType !== "streetFull" || reviewLocation !== "street" || !streetId,
		variables: {
			id: +streetId,
		},
	});

	return {
		streetInfo: data?.streetsOne || {},
		isLoadingStreetInfo: loading,
	};
};

const useDeclinationRegionQuery = (queryType, reviewLocation) => {
	const {
		params: { regionUrl },
	} = useWithDefaultParams();

	const { data, loading } = useQuery(DECLINATION_QUERY, {
		skip:
			(queryType !== "regionFull" && queryType !== "reviews") ||
			(reviewLocation !== "about_operator" &&
				reviewLocation !== "operator_actions" &&
				reviewLocation !== "operator_ratesmobile" &&
				reviewLocation !== "ratesmobile_region") ||
			!regionUrl,
		variables: {
			filter: `url=${regionUrl}`,
		},
	});

	return {
		regionInfo: data?.region || {},
		isLoadingRegionInfo: loading,
	};
};
